import { NavLink } from 'react-router-dom';
import {
  BarChart3,
  GitBranch,
  LayoutGrid,
  LifeBuoy,
  MessageSquare,
  Settings,
  Shield,
  Sparkles,
  Users,
} from 'lucide-react';
import { Logo } from '../ui/Logo.js';
import { hasCentralAccess, useSession } from '../auth/session.js';
import './sidebar.css';

interface NavItem {
  to: string;
  label: string;
  icon: typeof BarChart3;
  badge?: string;
  /** Shown to central access only; the API protects the routes regardless (technical §18.3). */
  central?: boolean;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

/**
 * Menu structure of the Backoffice. "Parcours utilisateur" is a single tab:
 * the former "Ederest Studio" and "Gestion des parcours" are merged into it.
 */
const GROUPS: NavGroup[] = [
  {
    title: 'Pilotage',
    items: [
      { to: '/', label: 'Tableau de bord', icon: LayoutGrid },
      { to: '/journeys', label: 'Parcours utilisateur', icon: GitBranch },
      { to: '/recommendations', label: 'Recommandations', icon: Sparkles, badge: 'IA' },
      { to: '/feedback', label: 'Avis recueillis', icon: MessageSquare },
    ],
  },
  {
    title: 'Administration',
    items: [
      { to: '/projects', label: 'Projets', icon: BarChart3, central: true },
      { to: '/accounts', label: 'Comptes', icon: Users, central: true },
      { to: '/script', label: 'Script', icon: Shield, central: true },
    ],
  },
  {
    title: 'Support',
    items: [
      { to: '/settings', label: 'Paramètres', icon: Settings },
      { to: '/help', label: 'Aide', icon: LifeBuoy },
    ],
  },
];

export function Sidebar({ projectName }: { projectName: string }) {
  const central = hasCentralAccess(useSession());
  const groups = GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.central || central),
  })).filter((group) => group.items.length > 0);

  return (
    <aside className="sidebar">
      <div className="sidebar__brand">
        <Logo suffix="Analytics" />
      </div>

      <nav className="sidebar__nav">
        {groups.map((group) => (
          <div key={group.title} className="sidebar__group">
            <p className="sidebar__group-title">{group.title}</p>
            {group.items.map(({ to, label, icon: Icon, badge }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                className={({ isActive }) => `sidebar__link${isActive ? ' sidebar__link--active' : ''}`}
              >
                <Icon size={16} strokeWidth={1.8} />
                <span>{label}</span>
                {badge && <span className="sidebar__badge">{badge}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="sidebar__foot">
        <div className="sidebar__project">
          <span className="sidebar__project-icon" aria-hidden>
            ◎
          </span>
          <span className="sidebar__project-text">
            <span className="sidebar__project-label">Projet actif</span>
            <span className="sidebar__project-name">{projectName}</span>
          </span>
        </div>
        <p className="sidebar__copy">© 2026 Ederest</p>
      </div>
    </aside>
  );
}
